"use strict";

import throwError from '@elements/throw-error';
import {addClass, removeClass, trigger} from "@elements/dom-utils";

const BODY_CLASSNAME = 'has-cookie-info';
export const EVENTS = {
    COOKIE_INFO_SHOWN: 'cookie-permissions/cookies-info-shown',
    COOKIE_INFO_CLOSED: 'cookie-permissions/cookies-info-closed',
    COOKIE_SETTINGS_SHOWN: 'cookie-permissions/cookies-settings-shown',
};

let _config = window['_config'] || {};
_config.cookieServices || (_config.cookieServices = {});
let permissionsFormStorage = {};
try {
    permissionsFormStorage = JSON.parse(localStorage.getItem('cookie-permissions'));
} catch (e) {
}

let servicePermissions = Object.keys(_config.cookieServices).reduce((servicesObject, serviceName) => {
    servicesObject[serviceName] = {
        isAllowed: !!_config.cookieServiceDefault,
        callbacks: [],
        ..._config.cookieServices[serviceName]
    };

    if (permissionsFormStorage && typeof permissionsFormStorage[serviceName] !== 'undefined') {
        servicesObject[serviceName].isAllowed = permissionsFormStorage[serviceName];
    }

    return servicesObject;
}, {});


const PIMCORE_TARGETING = 'pimcoreTargeting';
const PIMCORE_TARGETING_COOKIE_NAME = 'pimcore_targeting_disabled';
const COOKIE_DELETE_VALUE = '; expires=Thu, 01 Jan 1970 00:00:01 GMT;';

if (servicePermissions[PIMCORE_TARGETING]) {
    if (hasCookie(PIMCORE_TARGETING_COOKIE_NAME)) {
        let cookieValue = getCookie(PIMCORE_TARGETING_COOKIE_NAME);
        servicePermissions[PIMCORE_TARGETING].isAllowed = !cookieValue;
    } else {
        document.cookie = PIMCORE_TARGETING_COOKIE_NAME + '=' + (servicePermissions[PIMCORE_TARGETING].isAllowed ? COOKIE_DELETE_VALUE : '1');
    }
}


if (_config.registerPermissionGrantedCallback) {
    _config.registerPermissionGrantedCallback.forEach(function (obj) {
        registerPermissionGrantedCallback(obj.service, obj.callback);
    });
}

export function getServicePermission(serviceName) {
    if (typeof servicePermissions[serviceName] === 'undefined') {
        throwError('getServicePermission of undefined service "' + serviceName + '". ' +
            'Add the service to _config.cookieServices or use one of the existing ones: ',
            Object.keys(servicePermissions)
        );
    }

    return !!servicePermissions[serviceName] && !!servicePermissions[serviceName].isAllowed;
}

/* callback: passed callback is only called once */
export function registerPermissionGrantedCallback(serviceName, callback) {
    if (!serviceName || !callback) {
        throwError('registerPermissionGrantedCallback: wrong arguments serviceName:String, callback:Function');
        return;
    }

    if (getServicePermission(serviceName)) {
        try {
            callback();
        } catch (error) {
            console.error(error);
        }
    } else {
        if (servicePermissions[serviceName] && servicePermissions[serviceName].callbacks) {
            servicePermissions[serviceName].callbacks.push(callback);
        } else {
            throwError('registerPermissionGrantedCallback of undefined service "' + serviceName + '". ' +
                'Add the service to _config.cookieServices or use one of the existing ones: ',
                Object.keys(servicePermissions)
            );
        }
    }
}

export function setServicePermission(serviceName, isAllowed) {
    servicePermissions[serviceName].isAllowed = isAllowed;
    if (isAllowed) {
        notifyCallbacks(serviceName);
    }

    try {
        let permissionsFormStorage = JSON.parse(localStorage.getItem('cookie-permissions')) || {};

        permissionsFormStorage[serviceName] = isAllowed;

        localStorage.setItem('cookie-permissions', JSON.stringify(permissionsFormStorage));
    } catch (e) {
        console.error(e);
    }

    if (serviceName === PIMCORE_TARGETING) {
        document.cookie = PIMCORE_TARGETING_COOKIE_NAME + '=' + (isAllowed ? COOKIE_DELETE_VALUE : '1');
    }
}

function notifyCallbacks(serviceName) {
    if (servicePermissions[serviceName].callbacks) {
        let currentCallback;
        while (currentCallback = servicePermissions[serviceName].callbacks.shift()) {
            currentCallback();
        }
    }
}

export function trackPermissionChange() {
    if (window['dataLayer'] && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({
            event: 'cookie-permission-change'
        })
    }
}

function acceptAll() {
    Object.keys(servicePermissions).forEach(function (serviceName) {
        setServicePermission(serviceName, true);
    });
    trackPermissionChange();
}

function declineAll() {
    Object.keys(servicePermissions).forEach(function (serviceName) {
        setServicePermission(serviceName, false);
    });
    trackPermissionChange();
}

function wasCookieBarDismissed() {
    let isDismissed = false;
    try {
        isDismissed = !!localStorage.getItem('cookie-bar-dismissed');
    } catch (e) {
    }

    return isDismissed;
}

let externalFunctions;
export function init(options = {}) {
    if (!_config.cookieBar) {
        throwError(`@elements/cookie-permissions: Missing config "cookieBar". cookieBar was not set in _config`, window['_config']);
        return;
    }

    if (!options.showCookiesInfo) {
        throwError(`@elements/cookie-permissions: Init is missing a 'showCookiesInfo' parameter`);
    }

    if (!options.hideCookiesInfo) {
        throwError(`@elements/cookie-permissions: Init is missing a 'hideCookiesInfo' parameter`);
    }

    let uiServicePermissions = (wasCookieBarDismissed() || typeof _config.cookieServiceUIDefault === 'undefined')
        ? servicePermissions
        : Object.keys(servicePermissions).reduce(function (servicesObject, serviceName) {
            servicesObject[serviceName] = {
                ...servicePermissions[serviceName],
                ...(servicePermissions[serviceName].isReadOnly ? null : {isAllowed: _config.cookieServiceUIDefault})
            };

            return servicesObject;
        }, {});

    let props = {
        uiServicePermissions,
        onAcceptAll: () => {
            acceptAll();
            hideCookiesInfo();
        },
        onAcceptNecessary: () => {
            Object.keys(servicePermissions).forEach(serviceId => {
                setServicePermission(serviceId, servicePermissions[serviceId].hasOwnProperty('isAllowed')
                    ? servicePermissions[serviceId].isAllowed
                    : _config.cookieServiceDefault
                );
            });
            hideCookiesInfo();
            trackPermissionChange();
        },
        onDecline: () => {
            declineAll();
            hideCookiesInfo();
        },
        onPermissionUpdate: (permissions) => {
            permissions.forEach(function (permission) {
                setServicePermission(permission.name, permission.isAllowed);
            });
            hideCookiesInfo();
            trackPermissionChange();
        }
    };

    externalFunctions = {
        showCookiesInfo: () => options.showCookiesInfo(props),
        hideCookiesInfo: () => options.hideCookiesInfo(props),
        showCookiesSettings: () => options.showCookiesSettings
            ? options.showCookiesSettings(props)
            : options.showCookiesInfo(props)
    };

    if (!_config.hideCookiebar && !options.hideCookiebar){
        showCookiesInfo();
    }
}

/* options.force: if set, force the cookie bar to be shown even if it was already dismissed */
export function showCookiesInfo({force} = {}) {
    if (force) {
        addClass(BODY_CLASSNAME, document.body);
        return externalFunctions.showCookiesInfo();
    }

    let showCookieBar = !wasCookieBarDismissed();

    if (!showCookieBar && localStorage.getItem('cookie-permissions')) {
        let existingServices = Object.keys(JSON.parse(localStorage.getItem('cookie-permissions')));
        showCookieBar = !!(Object.keys(servicePermissions).filter(function (currentService) {
            return !existingServices.includes(currentService)
        }).length);
    }

    if (showCookieBar) {
        addClass(BODY_CLASSNAME, document.body);
        return externalFunctions.showCookiesInfo();
    }
}

export function hideCookiesInfo() {
    removeClass(BODY_CLASSNAME, document.body);

    externalFunctions.hideCookiesInfo();

    try {
        localStorage.setItem('cookie-bar-dismissed', true);
    } catch (e) {
    }
}

export function showCookiesSettings() {
    externalFunctions.showCookiesSettings();
}

// cookie helper
function getCookie(cname) {
    let name = cname + "=";
    let ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}

function hasCookie(cname) {
    return document.cookie.indexOf(cname) >= 0;
}

window.cookiePermissions = {
    ...window['cookiePermissions'],
    showCookiesSettings
};